import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import MobileLayout from "./component/layout/mobile-layout";
import WebLayout from "./component/layout/web-layout";
import Bill from "./component/Web_module/Bill";
import Dashboard from "./component/Web_module/Dashboard";
import HomePage from "./component/Web_module/Home";
import Login from "./component/Web_module/Login";
import ProtectedRoute from "./component/Web_module/ProtectedRoute";
import Register from "./component/Web_module/Register";
import Report from "./component/Web_module/Report";
import Settings from "./component/Web_module/Setting";
import AdminUserManagement from "./component/Web_module/AdminUserManagement";
import ProfileSettings from "./component/Web_module/SettingsSections/ProfileSettings";
import AddCustomerPage from "./component/Web_module/SettingsSections/AddCustomerPage";
import CustomerListPage from "./component/Web_module/SettingsSections/CustomerListPage";
import CustomerLeavePage from "./component/Web_module/SettingsSections/CustomerLeavePage";
import DairyInfoPage from "./component/Web_module/SettingsSections/DairyInfoPage";
import MilkRatePage from "./component/Web_module/SettingsSections/MilkRatePage";
import ExpensesPage from "./component/Web_module/SettingsSections/ExpensesPage";
import SalesPage from "./component/Web_module/SettingsSections/SalesPage";
import AllowancePage from "./component/Web_module/SettingsSections/AllowancePage";
import UserManagementPage from "./component/Web_module/SettingsSections/UserManagementPage";
import EmployeeManagementPage from "./component/Web_module/EmployeeManagementPage";
import RoleManagementPage from "./component/Web_module/RoleManagementPage";
import { LanguageProvider } from "./context/LanguageContext";
import useIsMobile from "./Hooks/useIsMobile";
import { authAPI, modulePermissionAPI } from "./services/api";

function AppRoutes() {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/" element={<HomePage key={location.key} />} />
      <Route path="/dashboard" element={<Dashboard key={location.key} />} />
      <Route path="/report" element={<Report key={location.key} />} />
      <Route path="/bill" element={<Bill key={location.key} />} />
      <Route path="/settings" element={<Settings key={location.key} />} />
      <Route path="/settings/profile" element={<ProfileSettings key={location.key} />} />
      <Route path="/settings/customer/add" element={<AddCustomerPage key={location.key} />} />
      <Route path="/settings/customer/list" element={<CustomerListPage key={location.key} />} />
      <Route path="/settings/customer/leaves" element={<CustomerLeavePage key={location.key} />} />
      <Route path="/settings/dairy-info" element={<DairyInfoPage key={location.key} />} />
      <Route path="/settings/milk-rate" element={<MilkRatePage key={location.key} />} />
      <Route path="/settings/expenses" element={<ExpensesPage key={location.key} />} />
      <Route path="/settings/sales" element={<SalesPage key={location.key} />} />
      <Route path="/settings/allowance" element={<AllowancePage key={location.key} />} />
      <Route path="/settings/users" element={<UserManagementPage key={location.key} />} />
      <Route path="/admin/users" element={<AdminUserManagement key={location.key} />} />
      <Route path="/admin/roles" element={<RoleManagementPage key={location.key} />} />
      <Route path="/settings/employee" element={<EmployeeManagementPage key={location.key} />} />
    </Routes>
  );
}

function App() {
  const isMobile = useIsMobile(768);
  const isAuthenticated = authAPI.isAuthenticated();

  useEffect(() => {
    const setVhCssVar = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVhCssVar();
    window.addEventListener("resize", setVhCssVar);
    window.addEventListener("orientationchange", setVhCssVar);

    return () => {
      window.removeEventListener("resize", setVhCssVar);
      window.removeEventListener("orientationchange", setVhCssVar);
    };
  }, []);

  // Permission sync ticker: re-reads permissions from the server
  // every 60 seconds and on tab focus, then forces a full re-render
  // so all components pick up the fresh permission list.
  const [permTick, setPermTick] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    const refresh = async () => {
      const user = authAPI.getCurrentUser();
      if (!user) return;
      try {
        const res = await modulePermissionAPI.getMyPermissions();
        if (res?.success && Array.isArray(res.data)) {
          user.permissions = res.data;
          localStorage.setItem("milkBillingUser", JSON.stringify(user));
          setPermTick(t => t + 1);
        }
      } catch { /* will retry */ }
    };

    refresh();
    const timer = setInterval(refresh, 60000);

    const onFocus = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [isAuthenticated]);

  // Global auto-calendar functionality for all date inputs
  useEffect(() => {
    const handleDateInputClick = (e) => {
      if (e.target.type === "date") {
        e.target.showPicker?.();
      }
    };

    // Add click event listener to document for all date inputs
    document.addEventListener("click", handleDateInputClick);

    return () => {
      document.removeEventListener("click", handleDateInputClick);
    };
  }, []);

  return (
    <LanguageProvider>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          }
        />
        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Register />
            )
          }
        />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {isMobile ? (
                <div className="mobile-layout">
                  <div className="mobile-scroll-area">
                    <AppRoutes />
                  </div>
                  <MobileLayout />
                </div>
              ) : (
                <div className="web-layout h-screen overflow-hidden flex">
                  <WebLayout />
                  <div className="web-main flex-1 flex flex-col overflow-hidden">
                    <AppRoutes />
                  </div>
                </div>
              )}
            </ProtectedRoute>
          }
        />
      </Routes>
    </LanguageProvider>
  );
}

export default App;
